import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { Alert } from '@interstate/components/Alert';
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';
import { EyeIcon } from '@interstate/components/Icons/EyeIcon';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { useCustomerCardSelector } from '../../../hooks/useCustomerCardSelector';
import * as customerSpdSelectors from '../../../store/customerSpdSelectors';
import { checkCustomerSpdRSAA } from '../../../store/customerSpdActionCreators';
import { getDealId, getDealerId } from '../../../store/selectors';
import { CUSTOMER_CARD_ANALYTICS } from '../../../analytics';
import { useGetSpd } from '../../../hooks/useGetSpd';
import SpdFields from './SpdFields';
import { SpdHeaderWrapper } from './spd.styles';
import type { CustomerSpdValues } from '../../../types/customerSpd.types';

const CustomerSpd = () => {
    const dispatch = useDispatch();
    const {
        isLocked,
        customerRole,
        setSpdValue,
        onAnalyticsEvent,
        formValues,
        spdValues,
        capabilities,
        isOperationDraft,
        hideCheckLoader
    } = useCustomerCardContext();
    const ccid = formValues.commonConsumerId as string;
    const spdRevealDisabled = capabilities?.enableSpdReveal === false;

    const rawPresence = useCustomerCardSelector(customerSpdSelectors.getSpdPresence(ccid));
    const presence = isOperationDraft ? undefined : rawPresence;
    const checkLoading = useCustomerCardSelector(customerSpdSelectors.getSpdCheckLoading);
    const checkError = useCustomerCardSelector(customerSpdSelectors.getSpdCheckError(ccid));
    const canDesk = useCustomerCardSelector(customerSpdSelectors.getHasDeskingPermission);
    const dealId = useCustomerCardSelector(getDealId);
    const dealerId = useCustomerCardSelector(getDealerId);

    const spdCheckStartRef = useRef<number | null>(null);

    useEffect(() => {
        if (checkLoading) {
            spdCheckStartRef.current = performance.now();
        } else if (spdCheckStartRef.current !== null) {
            const durationMs = Math.round(performance.now() - spdCheckStartRef.current);
            spdCheckStartRef.current = null;
            logNewRelicPageAction('SCI:SPDCheck - Duration', {
                durationMs,
                outcome: checkError ? 'failure' : 'success',
                hideCheckLoaderActive: Boolean(hideCheckLoader),
                dealerId: String(dealerId),
                dealXgId: dealId
            });
        }
    }, [checkLoading]);
    const [showValues, setShowValues] = useState(false);

    const { fetchSpd, isGetLoading, isGetError, isFetched } = useGetSpd(ccid);

    // SPD is read from the deal, so a drafted participant has nothing worth reading: a change points at
    // someone the deal has never seen, and a conversion is dropping the SPD it would return. Only the
    // drafted form goes quiet — the other participant keeps their own data.
    useEffect(() => {
        if (!isOperationDraft && !presence && !checkLoading) {
            dispatch(checkCustomerSpdRSAA(ccid));
        }
    }, []);

    useEffect(() => {
        if (!isOperationDraft && capabilities?.contractValidation && canDesk) {
            fetchSpd();
        }
    }, [capabilities?.contractValidation, canDesk, isOperationDraft]);

    // Becoming or ceasing to be a draft drops the revealed values, so the reveal closes with them —
    // left open, the fields render as blank inputs instead of returning to their masked state.
    useEffect(() => {
        setShowValues(false);
    }, [isOperationDraft]);

    const handleShowData = () => {
        const next = !showValues;
        setShowValues(next);
        if (next && !isFetched) {
            onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_SHOW_CUSTOMER_SPD);
            fetchSpd();
        }
    };

    const updatedCheck = useMemo(
        () => ({
            socialSecurityNumber: Boolean(presence?.socialSecurityNumber) || Boolean(spdValues.socialSecurityNumber),
            dateOfBirth: Boolean(presence?.dateOfBirth) || Boolean(spdValues.dateOfBirth),
            driversLicenseNumber: Boolean(presence?.driversLicenseNumber) || Boolean(spdValues.driversLicenseNumber),
            driversLicenseExpirationDate:
                Boolean(presence?.driversLicenseExpirationDate) || Boolean(spdValues.driversLicenseExpirationDate),
            driversLicenseState: presence?.driversLicenseState ?? spdValues.driversLicenseState ?? null
        }),
        [presence, spdValues]
    );

    const onChange = (field: keyof CustomerSpdValues, value: string) => {
        setSpdValue(field, value);
    };

    const isSpdDataAvailable = Boolean(
        presence?.socialSecurityNumber ||
            presence?.dateOfBirth ||
            presence?.driversLicenseNumber ||
            presence?.driversLicenseExpirationDate
    );

    return (
        <>
            <SpdHeaderWrapper>
                <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }} data-testid="customer-spd-header">
                    Additional Information
                </Typography>
                {!spdRevealDisabled && isSpdDataAvailable && canDesk && !isGetLoading && (
                    <Button
                        buttonStyle="tertiary"
                        size="small"
                        endIcon={<EyeIcon />}
                        onClick={handleShowData}
                        disabled={isLocked}
                        data-testid={`customer-spd-${customerRole}-${showValues ? 'hide' : 'show'}-data`}
                    >
                        {showValues ? 'Hide Data' : 'Show Data'}
                    </Button>
                )}
            </SpdHeaderWrapper>

            {!spdRevealDisabled && isGetError && (
                <Alert type="error" sx={{ marginBottom: '1rem' }} data-testid="customer-spd-get-error">
                    <Typography variant="body-sm" tag="span">
                        Something went wrong while processing your request. Please try again.
                    </Typography>
                </Alert>
            )}

            {!checkLoading && (
                /* The inputs seed their masked state on mount, so becoming or ceasing to be a draft
                   remounts them — presence is masked on the way in and restored on the way out. */
                <SpdFields
                    key={String(isOperationDraft)}
                    presence={presence}
                    updatedPresence={updatedCheck}
                    values={spdValues}
                    showValues={showValues}
                    loading={isGetLoading}
                    onChange={onChange}
                />
            )}
        </>
    );
};

export default CustomerSpd;
